import React, { useMemo, useRef, useState } from 'react';
import type { FormikErrors } from 'formik';
import styled from 'styled-components';
import { Add } from '@material-ui/icons';
import { IconButton } from '@workboard/wobo-ui';
import { useTranslation } from 'react-i18next';

const TopBarElement = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const List = styled.ul`
  margin: 16px 4px 32px 4px;
`;

const ListItem = styled.li`
  display: flex;
  border-bottom: 1px solid rgba(224, 224, 224, 1);
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  height: 40px;

  a {
    display: inline-block;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 100%;
    text-decoration: none;
  }

  &:hover,
  &:focus {
    background-color: #f7f8fa;
  }
`;

export type FieldHelpers<V> = {
  add(value: V): void;
  remove(id: string): void;
  update(id: string, value: Partial<V>): void;
};

export type RenderLineProps<V> = {
  id: string;
  value: V;
  error: FormikErrors<V>;
  isDisabled: boolean;
} & FieldHelpers<V>;

export type RenderFormProps<V> = {
  hide(): void;
  value: V[];
  errors: Array<FormikErrors<V>>;
  isDisabled: boolean;
} & FieldHelpers<V>;

interface ArrayFieldProps<V> {
  id: string;
  // eslint-disable-next-line react/no-unused-prop-types
  name: string;
  value: V[];
  onChange(e: React.ChangeEvent<unknown>, val: V[]): void;
  errors?: Array<FormikErrors<V>>;
  isDisabled?: boolean;
  renderLine(props: RenderLineProps<V>): React.ReactChild;
  renderForm(props: RenderFormProps<V>): React.ReactChild;
}

const createEvent = <V,>(value: V) =>
  ({
    target: {
      value,
    },
  } as any);

export const ArrayField = <V,>({
  id,
  value,
  onChange,
  errors,
  isDisabled,
  renderLine,
  renderForm,
}: ArrayFieldProps<V>) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>();
  const [formVisible, setFormVisible] = useState(false);
  const helpers = useMemo(
    () => ({
      add: (v: V) => {
        const newValue = [...value, v];
        onChange(createEvent(newValue), newValue);
      },
      remove: (itemId: string) => {
        const newValue = value.filter((_, idx) => String(idx) !== itemId);
        onChange(createEvent(newValue), newValue);
      },
      update: (itemId: string, v: V) => {
        const newValue = value.map((prevVal, idx) =>
          String(idx) === itemId ? { ...prevVal, ...v } : prevVal
        );
        onChange(createEvent(newValue), newValue);
      },
    }),
    [onChange, value]
  );
  return (
    <div id={id}>
      <TopBarElement>
        <h3>
          {value.length} {value.length === 1 ? `item` : `items`}
        </h3>

        <IconButton
          title={t('Add')}
          aria-label={t('Add item')}
          ref={buttonRef}
          disabled={formVisible}
          onClick={(e) => {
            e.preventDefault();
            setFormVisible(true);
          }}
          size="small"
          type="button"
          variant="ghost"
        >
          <Add />
        </IconButton>
      </TopBarElement>
      {formVisible ? (
        <div>
          {renderForm({
            hide: () => setFormVisible(false),
            ...helpers,
            value,
            errors,
            isDisabled,
          })}
        </div>
      ) : null}
      <List>
        {Object.entries(value).map(([itemId, val]) => {
          const error = errors?.[itemId];
          return (
            <ListItem key={itemId} tabIndex="-1">
              {renderLine({
                id: itemId,
                value: val,
                error,
                isDisabled,
                ...helpers,
              })}
            </ListItem>
          );
        })}
      </List>
    </div>
  );
};
